import {property_of} from '@bitstillery/common/lib/utils'
import m, {type Children, type ComponentType} from '@bitstillery/mithril'
import {unchecked} from '@bitstillery/common/lib/unchecked'

/**
 * Pure compile/resolve helpers shared by the page runtime (`page.tsx`) and the fragment registry
 * (`fragments.ts`). Kept dependency-free (no chrome imports) so layout components can import the
 * fragment registry without creating an import cycle back through `page.tsx`.
 */

/** Compile a CMS module body (blob `import`) and return its exports. */
export async function import_compiled_cms_module(compiled_js: string): Promise<Record<string, unknown>> {
    const blob = new Blob([compiled_js], {type: 'application/javascript'})
    const blob_url = URL.createObjectURL(blob)
    try {
        return unchecked<Record<string, unknown>>(await import(blob_url))
    } finally {
        URL.revokeObjectURL(blob_url)
    }
}

/** A bare CMS view: `function CmsView()` returning vnodes, which Mithril would mistake for a closure component. */
type CmsViewFunction = (vnode?: m.Vnode<unknown>) => Children

/** Only a class component has a view on its prototype. */
function is_class_component(v: unknown): v is ComponentType {
    if (typeof v !== 'function') {
        return false
    }
    const prototype: unknown = v.prototype
    return typeof prototype === 'object' && prototype !== null && 'view' in prototype && typeof prototype.view === 'function'
}

function is_component_like(v: unknown): v is ComponentType | CmsViewFunction {
    return typeof v === 'function' || (typeof v === 'object' && v !== null && typeof property_of(v, 'view') === 'function')
}

/**
 * Resolve the CMS component export from a compiled module and wrap it for Mithril.
 *
 * Bun TSX emits `export function CmsView` with no default, so the dynamic import `default` is
 * undefined — fall back to a `CmsView` named export, then any component-like export. Mithril treats
 * a plain `function` tag as a closure component (calls `Fn(vnode)` expecting `{ view }`), but CMS
 * TSX compiles to `function CmsView()` that returns vnodes directly, so wrap a bare function.
 */
export function resolve_cms_component(mod: Record<string, unknown>): ComponentType | undefined {
    let cmp: ComponentType | CmsViewFunction | undefined
    if (is_component_like(mod.default)) {
        cmp = mod.default
    } else if (is_component_like(mod.CmsView)) {
        cmp = mod.CmsView
    } else {
        for (const key of Object.keys(mod)) {
            if (key === 'default' || key === '__esModule') {
                continue
            }
            if (is_component_like(mod[key])) {
                cmp = mod[key]
                break
            }
        }
    }

    if (typeof cmp !== 'function') {
        return cmp
    }
    if (is_class_component(cmp)) {
        return cmp
    }
    // Any other function is a CMS view by the catalog's contract; nothing at runtime tells it from a closure component.
    const Fn = unchecked<CmsViewFunction>(cmp)
    return {
        view(vnode) {
            return Fn.length > 0 ? Fn(vnode) : Fn()
        },
    }
}

/** Compile a CMS module body into its Mithril component, or `undefined` when it has no usable export. */
export async function compile_cms_module(compiled_js: string): Promise<ComponentType | undefined> {
    const mod = await import_compiled_cms_module(compiled_js)
    return resolve_cms_component(mod)
}
